import { create } from 'zustand';
import type { GiftCardDto, GiftCardIssueRequest, GiftCardStatus, GiftCardSummaryDto } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';

/**
 * Gift cards (ADR-082) — searched and paged on the server; issued, voided and
 * resent here. Codes come back whole only from issuing, so the store keeps
 * the last batch to show until the sheet is closed.
 */

interface GiftCardsState {
  items: GiftCardDto[];
  nextCursor: string | null;
  total: number | null;
  search: string;
  status: GiftCardStatus | '';
  summary: GiftCardSummaryDto | null;
  loading: boolean;
  error: string | null;

  saving: boolean;
  saveError: string | null;
  /** The cards just issued, codes whole. */
  issued: GiftCardDto[] | null;

  load: (filters?: { search?: string; status?: GiftCardStatus | '' }) => Promise<void>;
  loadMore: () => Promise<void>;
  issue: (input: GiftCardIssueRequest) => Promise<boolean>;
  clearIssued: () => void;
  voidCard: (id: string, reason: string) => Promise<boolean>;
  resend: (id: string) => Promise<boolean>;
  clearSaveError: () => void;
}

const PAGE_SIZE = 20;

export const useGiftCardsStore = create<GiftCardsState>((set, get) => ({
  items: [],
  nextCursor: null,
  total: null,
  search: '',
  status: '',
  summary: null,
  loading: false,
  error: null,
  saving: false,
  saveError: null,
  issued: null,

  async load(filters = {}) {
    const search = filters.search ?? get().search;
    const status = filters.status ?? get().status;
    set({ loading: true, error: null, search, status });
    try {
      const [page, summary] = await Promise.all([
        withSession(() => adminApi.giftCards.list({ limit: PAGE_SIZE, ...(search.trim() ? { search: search.trim() } : {}), ...(status ? { status } : {}) })),
        withSession(() => adminApi.giftCards.summary()),
      ]);
      // A newer search answered first: this one is stale.
      if (get().search !== search || get().status !== status) return;
      set({ items: page.items, nextCursor: page.meta.nextCursor, total: page.meta.total ?? null, summary, loading: false });
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load the gift cards.') });
    }
  },

  async loadMore() {
    const { nextCursor, search, status, loading } = get();
    if (!nextCursor || loading) return;
    set({ loading: true });
    try {
      const page = await withSession(() =>
        adminApi.giftCards.list({ limit: PAGE_SIZE, cursor: nextCursor, ...(search.trim() ? { search: search.trim() } : {}), ...(status ? { status } : {}) }),
      );
      set((state) => ({ items: [...state.items, ...page.items], nextCursor: page.meta.nextCursor, loading: false }));
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load more gift cards.') });
    }
  },

  async issue(input) {
    set({ saving: true, saveError: null });
    try {
      const { cards } = await withSession(() => adminApi.giftCards.issue(input));
      set({ saving: false, issued: cards });
      await get().load();
      return true;
    } catch (error) {
      set({ saving: false, saveError: errorMessage(error, 'Could not issue the gift cards.') });
      return false;
    }
  },

  clearIssued() {
    set({ issued: null });
  },

  async voidCard(id, reason) {
    set({ saving: true, saveError: null });
    try {
      const card = await withSession(() => adminApi.giftCards.void(id, reason));
      set((state) => ({ saving: false, items: state.items.map((item) => (item.id === id ? card : item)) }));
      void get().load();
      return true;
    } catch (error) {
      set({ saving: false, saveError: errorMessage(error, 'Could not void the gift card.') });
      return false;
    }
  },

  async resend(id) {
    set({ saving: true, saveError: null });
    try {
      await withSession(() => adminApi.giftCards.resend(id));
      set({ saving: false });
      return true;
    } catch (error) {
      set({ saving: false, saveError: errorMessage(error, 'Could not send the gift card.') });
      return false;
    }
  },

  clearSaveError() {
    set({ saveError: null });
  },
}));
